import {
    AccountAccount,
    AccountConsent,
    AccountUserConsent,
    CircleCircle,
    CircleConsentConfig,
    CircleConsentConfigScopeEnum,
    CircleConsentTypes,
    CircleInvitation,
    CircleMergedConfig,
    CirclePartner,
    CircleValidationFieldWithValue,
} from '@calm/evexia-web-sdk';
import { AccessCodeState } from '@rhg/providers/access-code/types';
import { InviteCodeState } from '@rhg/providers/invite-code/types';
import { InvitationErrorType } from '@rhg/utils/invitations';
import {
    ConsentContextState,
    ConsentRequirement,
    ConsentTypes,
} from '../use-consent-context/types';

export enum RegistrationFlow {
    NewRegistration = 'newRegistration',
    ExistingRegistration = 'existingRegistration',
    NewInviteeRegistration = 'newInviteeRegistration',
    ExistingInviteeRegistration = 'existingInviteeRegistration',
    ChangeOfInviteeSponsorship = 'changeOfInviteeSponsorship',
    LossOfSponsorship = 'lossOfSponsorship',
    ChangeOfSponsorship = 'changeOfSponsorship',
    SkipSponsorship = 'skipSponsorship',
    PostMentalHealthScreeningPromo = 'postMentalHealthScreeningPromo',
}

export enum RegistrationFlowStep {
    ChangeSponsorship = 'changeSponsorship',
    SelectCountry = 'selectCountry',
    CreateCircle = 'createCircle',
    LostSponsorship = 'lostSponsorship',
    Hipaa = 'hipaa',
    AccessCode = 'accessCode',
    CCHDConsent = 'cchdConsent',
    PartnerAuthorization = 'partnerAuthorization',
    Eligibility = 'eligibility',
    Success = 'success',
    LinkingSSO = 'linkingSSO',
    ReceivingInvitation = 'receivingInvitation',
    EmailCommunicationPreferences = 'emailCommunicationPreferences',
    CountryConsent = 'countryConsent',
}

// The rest are unmapped since they do not fit smoothly into the flow
export const ConsentTypeRegistrationStepMap = {
    [ConsentTypes.Gina]: RegistrationFlowStep.PartnerAuthorization,
    [ConsentTypes.Chdpp]: RegistrationFlowStep.CCHDConsent,
    [ConsentTypes.UkInternational]: RegistrationFlowStep.CountryConsent,
};

export type SkippedSponsorshipStep =
    | RegistrationFlowStep.AccessCode
    | RegistrationFlowStep.Eligibility;

export interface CreateCircleProps {
    givenName: string;
    familyName: string;
    email: string;
    hasAgreedTerms: boolean;
}

export type ConsentVersions = { [C in CircleConsentTypes]?: number };
export interface SubmitFlowProps {
    eligibilityFormValues?: CircleValidationFieldWithValue[];
    registrationFlow?: RegistrationFlow;
    isSSO?: boolean;
    willUseSSOTokenEligibility?: boolean;
    isLinkingOnly?: boolean;
    minConsentVersions?: ConsentVersions;
    hipaaSignature?: string;
    circle?: CircleCircle;
}
export interface SubmitFlowResult {
    consentRequirements: ConsentRequirement;
    circle?: CircleCircle;
    account?: AccountAccount;
}
export type RegistrationForcedError = InvitationErrorType | 'generic';

type ChangeStep = (step: RegistrationFlowStep) => void;
type ChangeFlow = (flow: RegistrationFlow) => Promise<void>;

export interface RegistrationFlowContextProps {
    /** Whether all of the dependencies have been fetched */
    isFetched: boolean;
    /** The current flow the user is in (ex. existing user registration, loss of sponsorship) */
    registrationFlow: RegistrationFlow;
    /** The current step the user is in (ex. create circle, access code, CCHD) */
    currentStep: RegistrationFlowStep;
    /** Changes the current step */
    changeStep: ChangeStep;
    /** Changes the current flow (ex. user moves from loss of sponsorship to change sponsorship) */
    changeFlow: ChangeFlow;
    /** If the user has the partner portal object set in the JWT token, we know they are in the SSO flow */
    isSSO: boolean;
    /** If the user sub has prefix 'samlp', we know they are a SAML user and NOT a linked account or DTC user */
    isSamlUser: boolean;
    /** If the user has verified their email */
    isEmailVerified: boolean;
    /** if the user has gained sponsorship already */
    isPostEligibility: boolean;
    setIsPostEligibility: (value: boolean) => void;
    /** The sponsorship api call(s) within the submit flow are in flight */
    submitIsPending: boolean;
    /** Error on submit flow */
    submitFlowError: Error | null;
    submitFlowReset: () => void;
    /** if the user has a current sponsorship, have they lost it? */
    hasLostSponsorship: boolean;
    /** The current sponsor the user's circle is associated with */
    currentSponsor?: CircleMergedConfig;
    /** The partner associated with currentSponsor */
    currentSponsorPartner?: CirclePartner;
    /** The dependent partner associated with currentSponsor */
    currentSponsorDependentPartner?: CirclePartner;
    /** The sponsor the user is trying to associate with based on the access code they have */
    accessCodeSponsor?: CirclePartner;
    /** The user's account */
    account: AccountAccount;
    /** Consents the user needs to accept */
    accountConsents: AccountConsent[];
    /** The state of the user's consent acceptance */
    accountUserConsents: AccountUserConsent[];
    /** The state of the user's access code (usually from useAccessCode() )*/
    accessCodeState: AccessCodeState;
    /** The state of the user's invitation (usually from useInviteCode() )*/
    inviteCodeState: InviteCodeState;
    /** The user's invitation object if they have an invite code */
    invitation?: CircleInvitation;
    /** If there was an identified invitaiton error when getting the user's invitation it is stored here (ex. claimed, expired, country) */
    invitationError?: InvitationErrorType;
    /** The sponsor the user is trying to associate with based on the invitaiton they have */
    invitationSponsor?: CirclePartner;
    /** If there was an identified invitaiton error when getting the user's invitation partner it is stored here (ex. claimed, expired, country) */
    invitationSponsorError?: InvitationErrorType;
    /** Called to create the user's circle (usually on the TOS screen) */
    createCircle: (props: CreateCircleProps) => Promise<void>;
    /** Represents whether a creaceCircle call is currently in progress */
    createCircleLoading?: boolean;
    /** Error on create circle */
    createCircleError: Error | null;
    createCircleReset: () => void;
    /** Called to validate the user's access code (usually on the access code screen or on TOS) */
    validateAccessCode: (
        code: string,
        willSetAccessCode?: boolean,
        circle?: CircleCircle | undefined
    ) => Promise<CirclePartner | null>;
    /** Called to validate the user's invite code (usually on the invite code screen (TBD) or on TOS) */
    validateInvitationCode: (
        inviteCode: string
    ) => Promise<CircleInvitation | null>;
    /** Called to verify the user's email (usually on the success screen) */
    verifyEmail: () => Promise<void>;
    /** Called to submit the user's registration data (usually on the eligibility screen) */
    submitFlow: (
        props: SubmitFlowProps
    ) => Promise<
        { circle?: CircleCircle; account?: AccountAccount } | undefined
    >;
    /** Called to complete the flow and send the user elsewhere, the registration data should already be submitted at this time (usually on the success screen) */
    completeFlow: () => Promise<void>;
    /** Called to exit the flow (ex. close the dialog on existing user registrations) */
    exitFlow?: (actions?: {
        changeStep: ChangeStep;
        changeFlow: ChangeFlow;
    }) => Promise<void>;
}

export type AccountConsentTypes = 'tos' | 'privacy' | 'chdpp';

export interface AccountConsentVersions {
    tos: number;
    privacy: number;
    chdpp: number;
}

export type CircleConsentVersions = Record<CircleConsentTypes, number>;
export type CircleConsentScopes = Record<
    CircleConsentTypes | AccountConsentTypes,
    CircleConsentConfigScopeEnum
>;

export interface AccountSponsorUserConsentRequirementProps {
    accountConsents: AccountConsent[];
    accountUserConsents: AccountUserConsent[];
    sponsorConsents: CircleConsentConfig[] | undefined;
    currentSponsorConsents: CircleConsentConfig[] | undefined;
    registrationFlow: RegistrationFlow | undefined;
}

export type RegistrationFlowContextState = RegistrationFlowContextProps &
    ConsentContextState & {
        /** Next needed consent step based on requirements */
        nextConsentStep?: RegistrationFlowStep;
        /** The sponsor object either invitationSponsor or accessCodeSponsor */
        newSponsor?: CirclePartner;
        /** The sponsor object based on the user's access code */
        accessCodeSponsor?: CirclePartner;
        /** Whether the user has set their country */
        hasSetCountry: boolean;

        /** Track if the user skipped eligibility or access code steps so we know what dialog messaging to show */
        skippedSponsorshipStep?: SkippedSponsorshipStep;
        setSkippedSponsorshipStep: (value: SkippedSponsorshipStep) => void;

        /** force an error to show in the registration container or elsewhere */
        forcedError?: RegistrationForcedError;
        setForcedError: (forcedError?: RegistrationForcedError) => void;
    };

/** For some reason declaring this function here makes it miraculously work (See https://calmdotcom.atlassian.net/browse/EVE-8642) */
export function getConsentRegistrationStepMiraculouslyWorks(
    consentType?: ConsentTypes
) {
    return consentType && ConsentTypeRegistrationStepMap[consentType]
        ? ConsentTypeRegistrationStepMap[consentType]
        : undefined;
}
